import { parseTenantEndpoint, type TenantEndpoint } from '@core/session/tenantEndpoint';

// The rules about which server a password may go to live in the shared core, so the web and the
// mobile app enforce the same ones; this file keeps only the browser's way of remembering a company.
export * from '@core/session/tenantEndpoint';

/**
 * Versioned, so a stored shape from an older build is ignored rather than misread. Bumping the
 * suffix is how this module discards its own history without a migration.
 */
const storageKey = 'attirerent.tenant.v1';

/**
 * The company remembered on this device, if any.
 *
 * `localStorage` rather than `sessionStorage` so a counter clerk opening the till in the morning
 * lands on the password step rather than re-typing the company code every day. Nothing here is a
 * credential — the code and name are printed on the shop's own paperwork, and the URL is public
 * routing — so this is not the storage decision the refresh token deliberately avoided.
 */
export function loadTenantEndpoint(): TenantEndpoint | null {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(storageKey);
  } catch {
    // Storage disabled, or a private window that refuses it. Not remembering is a fine outcome;
    // failing to render the sign-in screen is not.
    return null;
  }

  if (raw === null) {
    return null;
  }

  try {
    return parseTenantEndpoint(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveTenantEndpoint(endpoint: TenantEndpoint): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(endpoint));
  } catch {
    // Remembering is a convenience. A quota or a privacy setting refusing it must not take
    // sign-in down with it.
  }
}

/** Forgets the company — "change company" on the credentials step, and signing out of one. */
export function clearTenantEndpoint(): void {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // As above: nothing downstream depends on this having worked.
  }
}
